import * as React from 'react';

import {withSystem, withMeta, TypeImplementation} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  forwardDispatch,
} from '@packages/systems/ExpressionEditor-utils';
import {TRecord, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {pluginName, typeName, qualify} from '@packages/systems/wfdl-Qualified';

import {VisuallyHidden} from '@packages/systems/spring';
import TooltipControl from '@packages/systems/design-system/TooltipControl';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {LogicLabel} from '@packages/systems/flows/shared';
import {TEmail} from '@packages/systems/plugins/Email';

import {StyledHelpIcon} from './icons';

const {getRecord} = ContextualExpressions;

const StyledContainer = emotionStyled('div')({
  display: 'grid',
  rowGap: tokens.space[2],
});

const tooltipTextEmail =
  'The recipient will receive an email invitation to create a new account with a link to the standard sign up page.';
const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56, // 224
}));

const StyledHelpIconPadded = styled(StyledHelpIcon)(({theme: {gutters}}) => ({
  paddingLeft: gutters.x4,
}));

const emailAddressPath = {in: 'Record', at: 'Email'} as const;
const accessGroupsPath = {in: 'Record', at: 'AccessGroups'} as const;
const typeOfInviteUser = TRecord({
  Email: withSystem(
    {
      isFullWidth: true,
      label: 'Email address',
      labelVariant: 'hidden',
    },
    withMeta({placeholder: 'E.g. john@doe.com'}, TEmail)
  ),
  AccessGroups: withSystem(
    {
      isFullWidth: true,
      label: 'Access groups',
      labelVariant: 'hidden',
      bindUIVariant: 'custom',
    },
    TTypeConstructor(
      qualify(pluginName('UsersPlugin'), typeName('AccessGroupsAliasType'))
    )
  ),
});

const getPaths = (annExpr: ContextualExpression) => {
  const rec = getRecord(stripAnn(annExpr));
  const emailExpr = rec?.Email;
  const accessGroupListExpr = rec?.AccessGroups;

  if (emailExpr == null || accessGroupListExpr == null) {
    return null;
  }

  return {
    emailExpr,
    accessGroupListExpr,
  };
};

export const InviteUser = TypeImplementation.Alias({
  type: typeOfInviteUser,
  system: {bindUIVariant: 'custom'},
  render: function RenderInviteUser({expr, dispatch}) {
    const paths = React.useMemo(() => getPaths(expr), [expr]);

    if (paths == null) {
      return <ExpressionEditor dispatch={dispatch} expression={expr} />;
    }

    return (
      <StyledContainer>
        <LogicLabel label="Email" required>
          <VisuallyHidden>{tooltipTextEmail}</VisuallyHidden>
          {/* @ts-expect-error - TS2739 - Type '{ "aria-hidden": true; icon: Element; content: Element; alternate: true; }' is missing the following properties from type '{ [x: string]: any; iconStyle?: { padding: number; boxSizing: string; } | undefined; icon?: Element | undefined; cursor?: string | undefined; placement?: string | undefined; children: any; hideOnClick: any; style: any; automationLabel?: string | undefined; }': children, hideOnClick, style */}
          <TooltipControl
            aria-hidden
            icon={<StyledHelpIconPadded />}
            content={<TooltipContent>{tooltipTextEmail}</TooltipContent>}
            alternate
          />
          <ExpressionEditor
            dispatch={forwardDispatch(dispatch)(emailAddressPath)}
            expression={paths.emailExpr}
          />
        </LogicLabel>

        <ExpressionEditor
          expression={paths.accessGroupListExpr}
          dispatch={forwardDispatch(dispatch)(accessGroupsPath)}
        />
      </StyledContainer>
    );
  },
});
